import { useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth.ts";
import { API_URL } from "constants/index.ts";
import { useSession } from "@clerk/clerk-react";
import { Input, Listbox, ListboxItem, Spacer } from "@heroui/react";
import { Search } from "assets/icons";

interface MatchedSubstring {
  length: number;
  offset: number;
}

interface StructuredFormatting {
  main_text: string;
  main_text_matched_substrings: MatchedSubstring[];
  secondary_text: string;
}

interface Term {
  offset: number;
  value: string;
}

interface PlaceSuggestion {
  description: string;
  matched_substrings: MatchedSubstring[];
  place_id: string;
  reference: string;
  structured_formatting: StructuredFormatting;
  terms: Term[];
  types: string[];
}

export const getMapsURL = (address: string): string => {
  const encodedAddress = encodeURIComponent(address);

  // Detecting iOS and Android devices
  const userAgent = navigator.userAgent || navigator.vendor;
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) && !(navigator as any).standalone;
  const isAndroid = /android/i.test(userAgent);

  if (isIOS) {
    return `https://maps.apple.com/?address=${encodedAddress}`;
  } else if (isAndroid) {
    return `geo:0,0?q=${encodedAddress}`;
  } else {
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }
};

interface AddressPickerProps {
  getAddress?: (selectedPlace: {
    description: string;
    placeId: string;
  }) => void;
  defaultAddress?: string;
  onSelect?: () => void;
  label?: string;
  editMode?: boolean;
  selectedValue?: any;
  autoSelect?: boolean;
  placeholder?: string | null;
}

export const AddressPicker = ({
  getAddress,
  defaultAddress = "",
  onSelect,
  label,
  editMode = false,
  autoSelect = false,
  placeholder = null
}: AddressPickerProps) => {
  const session = useSession();
  const [value, setValue] = useState<string>(defaultAddress);
  const [results, setResults] = useState<PlaceSuggestion[]>([]);
  const [showList, setShowList] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);


  useEffect(() => {
    if (!defaultAddress || defaultAddress == "" || !autoSelect) {
      console.log("No default address or no autoselect");
      return;
    } 
    const getLocation = async () => {
      const response = await fetchWithAuth(
        `${API_URL}/places/autocomplete?input=${encodeURIComponent(value)}`,
        { method: "GET" },
        session.session,
      );
      const { predictions } = await response.json();
      if(predictions && predictions.length > 0) {
        getAddress && getAddress(predictions[0]);
        setValue(predictions[0].description);
        (inputRef.current as any).value = predictions[0].description;
        console.log("Auto setting address from business card scanner")
        console.log(predictions[0]);
        onSelect && onSelect();
      }

    };
    const timeout = setTimeout(() => {
      getLocation();
    }, 300);

    return () => clearTimeout(timeout);
  }, [defaultAddress]);

  useEffect(() => {
    setValue(defaultAddress);
  }, [defaultAddress]);

  useEffect(() => {
    if (value.length < 4 || !isEditing) return;
    const getLocation = async () => {
      const response = await fetchWithAuth(
        `${API_URL}/places/autocomplete?input=${encodeURIComponent(value)}`,
        { method: "GET" },
        session.session,
      );
      const { predictions } = await response.json();
      setResults(predictions);
      console.log(predictions);
      setShowList(true);
    };
    const timeout = setTimeout(() => {
      getLocation();
    }, 300);

    return () => clearTimeout(timeout);
  }, [session.session, value, isEditing]);

  const handleValueChange = (value: any) => {
    setValue(value);
  }

  return (
    <div className="">
      {editMode ? (
        <>
          <Input
            labelPlacement="outside"
            label={label}
            startContent={<Search className="h-5 w-5" />}
            isClearable
            onBlur={() => {
              setTimeout(() => {
                setShowList(false);
              }, 200);
            }}
            ref={inputRef}
            placeholder={placeholder || "Voer adres in"}
            value={value ?? inputRef?.current?.value}
            onValueChange={(value) => {
              handleValueChange(value);
              setIsEditing(true);
            }}
            type="text"
            classNames={{
              input:
                "text-neutral-primary placeholder:text-neutral-secondary-mid",
              inputWrapper:
                "p-2 min-h-fit h-fit rounded-full bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
            }}
          />
          <Spacer y={1} />
          {showList && (
            <Listbox
              className="absolute z-10 w-full rounded-md bg-white shadow-md"
              selectionMode="single"
              aria-label="address-picker"
              onAction={(key) => {
                console.log(key);
                const selectedPlace = results.find(
                  (result) => result.description === key,
                );
                console.log(selectedPlace);
                if (inputRef.current && selectedPlace) {
                  inputRef.current.value = selectedPlace.description;
                  getAddress &&
                    getAddress({
                      description: selectedPlace.description,
                      placeId: selectedPlace.place_id,
                    });
                }
                setShowList(false);
                setIsEditing(false);
                if (onSelect) onSelect();
              }}
            >
              {results.map((result) => (
                <ListboxItem key={result.description}>
                  {result.description}
                </ListboxItem>
              ))}
            </Listbox>
          )}
        </>
      ) : value ? <a
        href={getMapsURL(value)}
        className="flex items-center justify-center text-moss-primary"
        target="_blank" // Opens in a new tab
        rel="noopener noreferrer" // For security reasons
      >
        {value}
      </a>
        : <p className="font-medium">{"Niet ingevuld"}</p>
      }
    </div>
  );
};
